import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { post, ApiError } from '@/lib/api';
import { APPROVE_WARNING, pendingLogoIds, reasonLabel, type LiveArtPending } from '@/lib/live-art';
import { Thumbnail } from './Thumbnail';
import type { TeamStoreDetail } from './StoreEditorDrawer';

type ApproveBody = {
  success?: boolean;
  message?: string;
  error?: string;
  reprint?: { complete?: boolean } | null;
};

function reason(e: unknown, fallback: string): string {
  const body = e instanceof ApiError ? (e.body as { message?: string; error?: string } | null) : null;
  return body?.message || body?.error || fallback;
}

/**
 * "New art saved — not live yet". A logo replace / caption edit on a selling
 * store waits here: the store keeps selling the OLD print until staff approve,
 * which reprints the logo products IN PLACE (POST .../live-art/approve).
 */
export function LiveArtBanner({ store, refresh }: { store: TeamStoreDetail & { liveArtPending?: LiveArtPending }; refresh: () => Promise<unknown> }) {
  const [busy, setBusy] = useState(false);
  const pending = pendingLogoIds(store.liveArtPending);
  if (pending.length === 0) return null;
  const reasons = store.liveArtPending?.reasons ?? {};
  const logos = store.logos ?? [];

  async function approve() {
    if (!window.confirm(`Approve the new art for live?\n\n${APPROVE_WARNING}`)) return;
    setBusy(true);
    try {
      const r = await post<ApproveBody>(`/api/team-stores/${store.id}/live-art/approve`, { logoIds: pending });
      // 207 = reprinted some, not all: the reasons are in message.
      if (r.success === false) toast.warning(r.message || 'Some products were not reprinted');
      else toast.success(r.message || 'Reprint finished — the new art is live');
      await refresh();
    } catch (e) {
      toast.error(reason(e, 'Approve for live failed'));
      await refresh();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-[14px] border border-warning/40 bg-warning/10 p-4" role="status">
      <h3 className="text-[13px] font-semibold text-warning">New art saved — not live yet</h3>
      <p className="mt-1 text-[11.5px] text-muted">
        The store keeps selling the old print until you approve the new art for live.
      </p>
      <div className="mt-3 flex flex-wrap gap-3">
        {pending.map((id) => {
          const logo = logos.find((l) => (l.id ?? l.logoId) === id);
          return (
            <div key={id} className="w-28 text-center">
              {logo?.gcsPath ? (
                <Thumbnail path={logo.gcsPath} alt={logo.label ?? id} className="mx-auto h-16 w-24" />
              ) : (
                <div className="mx-auto flex h-16 w-24 items-center justify-center rounded-[8px] bg-bg text-[10px] text-subtle">—</div>
              )}
              <p className="mt-1 truncate text-[10.5px] text-muted">
                {logo?.label ?? id} — {reasonLabel(reasons[id])}
              </p>
            </div>
          );
        })}
      </div>
      <div className="mt-3">
        <Button size="sm" disabled={busy} onClick={() => void approve()}>
          {busy ? 'Reprinting…' : 'Approve for live'}
        </Button>
      </div>
    </div>
  );
}
